import React, { useState } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import FilmInitiative from './FilmInitiative';

// --- CONFIGURATION DATA ---
const slateData = {
  founder: "Michael Kuper",
  contactEmail: "hello@apertureslate.org",
  googleScriptUrl: "https://script.google.com/macros/s/AKfycbwUgJp0ShzOefL6Ik194PCzG0xP_svobBtfQ3qsi3HoVLD19hspcz7CT0_IkSfOBPuIAA/exec",
  fracturedAtlasUrl: "https://fundraising.fracturedatlas.org/aperture-slate-production-slate",
  prospectusUrl: "/The-Aperture-Slate-Engine.pdf", 
  socials: [
    { name: "INSTAGRAM", url: "https://www.instagram.com/apertureslate/" },
    { name: "THREADS", url: "https://www.threads.com/@apertureslate" }
  ],
  updates: [
    { date: "MAR 15, 2026", text: "Phase I Commissioning Series officially enters pre-production.", tag: "PRODUCTION" },
    { date: "MAR 02, 2026", text: "Maternal script receives final polish. Casting begins April.", tag: "DEVELOPMENT" },
    { date: "FEB 20, 2026", text: "Fractured Atlas sponsorship officially live for tax-deductible giving.", tag: "FINANCE" }
  ],
  awards: [
    "Best Screenwriter - Orlando Film Festival",
    "Best Military Film - San Diego Film Festival",
    "15+ Years Industry Experience (NBC, HBO, Universal)"
  ],
  projects: [
    {
      title: "MATERNAL",
      status: "IN DEVELOPMENT",
      type: "Feature Film",
      description: "A gripping psychological exploration of maternal bonds under extreme pressure.",
      image: "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&q=80&w=800"
    },
    {
      title: "RAILWAY SPINE",
      status: "DISTRIBUTED // WALMART",
      type: "Award-Winning Narrative",
      description: "Critically acclaimed military drama exploring the trauma of conflict. Successfully secured national retail distribution via Walmart and major VOD platforms.",
      image: "https://images.unsplash.com/photo-1478720568477-152d9b164e26?auto=format&fit=crop&q=80&w=800"
    },
    {
      title: "COMMISSIONING SERIES",
      status: "PHASE I",
      type: "Slate Initiative",
      description: "Providing infrastructure and capital for the next generation of visionary filmmakers.",
      image: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&q=80&w=800"
    }
  ],
  faq: [
    {
      q: "What is a 'Production Slate'?",
      a: "Unlike a single-film investment, a slate spreads capital across multiple projects in various stages of production. This diversifies the creative portfolio and ensures a constant pipeline of content."
    },
    {
      q: "How are projects selected?",
      a: "Projects are vetted based on narrative strength, market viability, and the unique perspective of the filmmaker. We prioritize stories that challenge mainstream conventions while maintaining high production values."
    },
    {
      q: "What is the distribution strategy?",
      a: "We leverage established relationships with retail giants like Walmart and major VOD platforms, as proven by our previous work, to ensure each project finds its global audience."
    }
  ]
};

// --- STYLING ---
const styles = {
  body: { backgroundColor: '#050505', color: '#ffffff', fontFamily: 'Inter, system-ui, sans-serif', margin: 0, scrollBehavior: 'smooth' },
  nav: { position: 'fixed', top: 0, width: '100%', padding: '15px 5%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 1000, backgroundColor: 'rgba(5,5,5,0.95)', backdropFilter: 'blur(15px)', boxSizing: 'border-box', borderBottom: '1px solid #1a1a1a' },
  logoImg: { height: '50px', width: 'auto' },
  section: { padding: '80px 10%', minHeight: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', position: 'relative' },
  heroTitle: { fontSize: 'clamp(2.5rem, 8vw, 5.5rem)', fontWeight: '900', letterSpacing: '-0.04em', lineHeight: '0.9', margin: '20px 0', textTransform: 'uppercase' },
  accent: { color: '#00aaff', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.4em', fontWeight: 'bold' },
  trustBar: { padding: '40px 10%', backgroundColor: '#080808', display: 'flex', justifyContent: 'space-around', alignItems: 'center', flexWrap: 'wrap', gap: '20px', opacity: 0.5, borderBottom: '1px solid #1a1a1a' },
  trustLogo: { fontWeight: '800', fontSize: '1.2rem', letterSpacing: '0.1em', color: '#fff' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '30px', marginTop: '50px' },
  card: { border: '1px solid #1a1a1a', backgroundColor: '#0a0a0a', overflow: 'hidden' },
  btn: { padding: '18px 36px', border: 'none', background: '#fff', color: '#000', cursor: 'pointer', marginTop: '10px', fontWeight: '800', letterSpacing: '0.05em', textTransform: 'uppercase', width: '100%', transition: '0.3s', textDecoration: 'none', display: 'inline-block', textAlign: 'center' },
  btnOutline: { padding: '18px 36px', border: '1px solid #333', background: 'transparent', color: '#fff', cursor: 'pointer', marginTop: '10px', fontWeight: '800', letterSpacing: '0.05em', textTransform: 'uppercase', width: '100%', transition: '0.3s', textDecoration: 'none', display: 'block', textAlign: 'center', boxSizing: 'border-box' },
  input: { padding: '15px', width: '100%', backgroundColor: '#111', border: '1px solid #333', color: '#fff', marginBottom: '10px', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' },
  faqItem: { marginBottom: '40px', borderBottom: '1px solid #111', paddingBottom: '20px' },
  fiscalNote: { fontSize: '0.9rem', color: '#888', lineHeight: '1.6', marginTop: '12px' },
  donateBox: { border: '1px solid #222', padding: '30px', backgroundColor: '#0a0a0a', maxWidth: '480px' },
  downloadBox: { border: '1px solid #333', padding: '40px', backgroundColor: '#080808', maxWidth: '480px', textAlign: 'left' },
  checkNote: { fontSize: '0.95rem', color: '#aaa', lineHeight: '1.6', marginTop: '16px', borderTop: '1px solid #1a1a1a', paddingTop: '16px' },
  navSocial: { fontSize: '0.7rem', color: '#00aaff', textDecoration: 'none', letterSpacing: '0.1em', marginLeft: '15px', fontWeight: 'bold' }
};

// --- MODAL COMPONENT ---
const WaitlistModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    try {
      await fetch(`${slateData.googleScriptUrl}?email=${encodeURIComponent(email)}`, {
        method: 'POST',
        mode: 'no-cors'
      });
      setStatus('success');
      setTimeout(() => { onClose(); setStatus('idle'); setEmail(''); }, 2500);
    } catch (error) {
      console.error("Submission error", error);
      setStatus('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.98)', zIndex: 3000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', boxSizing: 'border-box' }}>
      <div style={{ maxWidth: '450px', width: '100%', textAlign: 'center', border: '1px solid #222', padding: '50px', backgroundColor: '#050505', boxSizing: 'border-box' }}>
        <img src="/logo.png" alt="Aperture Slate" style={{ height: '60px', marginBottom: '20px' }} />
        <h2 style={{ letterSpacing: '0.2em', fontSize: '1.2rem' }}>PRIVATE SLATE ACCESS</h2>
        {status === 'success' ? (
          <div style={{ margin: '40px 0' }}>
            <p style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1rem' }}>REGISTRATION COMPLETE</p>
            <p style={{ color: '#666', fontSize: '0.9rem' }}>You have been added to the 2026 Prospectus list.</p>
          </div>
        ) : (
          <>
            <p style={{ color: '#888', fontSize: '0.9rem', lineHeight: '1.6', margin: '25px 0' }}>Join the waitlist to receive our 2026 Prospectus and an invitation to the official fundraising launch.</p>
            <form onSubmit={handleSubmit}>
              <input type="email" placeholder="Email Address" required style={styles.input} value={email} onChange={(e) => setEmail(e.target.value)} />
              <button type="submit" disabled={status === 'loading'} className="btn-hover" style={{...styles.btn, opacity: status === 'loading' ? 0.5 : 1, width: '100%'}}>
                {status === 'loading' ? 'REGISTERING...' : 'REQUEST ACCESS'}
              </button>
            </form>
          </>
        )}
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#444', cursor: 'pointer', marginTop: '20px', fontSize: '0.8rem', textDecoration: 'underline' }}>RETURN TO SITE</button>
      </div>
    </div>
  );
};

// --- MAIN APP ---
function MainSite() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div style={styles.body}>
      <style>{`
        @keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.4; } 100% { opacity: 1; } }
        .live-tag { animation: pulse 2s infinite; color: #ff3b3b !important; }
        .btn-hover:hover { background: #00aaff !important; color: #fff !important; box-shadow: 0 0 20px rgba(0,170,255,0.4) !important; }
        .social-card:hover { border-color: #00aaff !important; background: #0c0c0c !important; }
      `}</style>

      <WaitlistModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />

      <nav style={styles.nav}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <img src="/logo.png" alt="Aperture Slate Logo" style={styles.logoImg} />
          <div style={{ fontWeight: '900', letterSpacing: '0.2em', fontSize: '1.1rem' }}>APERTURE SLATE</div>
        </div>
        <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          <div style={{ fontSize: '0.65rem', letterSpacing: '0.1em', opacity: 0.6 }}>{slateData.founder.toUpperCase()} // EXECUTIVE DIRECTOR</div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <a href="/#/submit" style={{ ...styles.navSocial, color: '#fff', backgroundColor: '#00aaff', padding: '4px 10px', marginLeft: '15px' }}>
              2027 INITIATIVE ★
            </a>
            {slateData.socials.map((s, i) => (
              <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" style={styles.navSocial}>
                {s.name}
              </a>
            ))}
          </div>
        </div>
      </nav>

      {/* HERO SECTION */}
      <section style={{ ...styles.section, minHeight: '80vh', paddingTop: '160px', paddingBottom: '100px' }}>
        <span style={styles.accent}>Establishing the Frame</span>
        <h1 style={styles.heroTitle}>A $1M PRODUCTION<br/><span style={{color: '#fff'}}>SLATE ENGINE</span></h1>
        <p style={{ maxWidth: '650px', fontSize: '1.3rem', color: '#888', lineHeight: '1.5', marginTop: '20px' }}>
          Providing the capital, professional infrastructure, and artistic oversight for a continuous pipeline of visionary independent films.
        </p>
        <div style={{ maxWidth: '300px' }}>
          <button className="btn-hover" style={styles.btn} onClick={() => setIsModalOpen(true)}>JOIN THE MISSION</button>
        </div>
      </section>

      {/* UPDATES FEED */}
      <section style={{ ...styles.section, backgroundColor: '#050505', borderTop: '1px solid #111', paddingBottom: '40px' }}>
        <span style={styles.accent}>Field Notes</span>
        <h2 style={{ fontSize: '2rem', fontWeight: '900', margin: '15px 0 40px 0' }}>LATEST UPDATES</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
          {slateData.updates.map((update, i) => (
            <div key={i} className="social-card" style={{ padding: '25px', border: '1px solid #1a1a1a', backgroundColor: '#080808', transition: '0.3s' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <span style={{ fontSize: '0.7rem', color: '#00aaff', fontWeight: 'bold' }}>{update.tag}</span>
                <span style={{ fontSize: '0.7rem', color: '#444' }}>{update.date}</span>
              </div>
              <p style={{ fontSize: '0.95rem', lineHeight: '1.6', color: '#bbb', margin: 0 }}>{update.text}</p>
            </div>
          ))}
        </div>
      </section>

      <div style={styles.trustBar}>
        <span style={styles.trustLogo}>WALMART</span>
        <span style={styles.trustLogo}>NBC</span>
        <span style={styles.trustLogo}>HBO</span>
        <span style={styles.trustLogo}>UNIVERSAL</span>
      </div>

      <section id="slate" style={{ ...styles.section, backgroundColor: '#080808' }}>
        <span style={styles.accent}>Cinematic Pipeline</span>
        <h2 style={{ fontSize: '3.5rem', fontWeight: '900' }}>THE SLATE</h2>
        <div style={styles.grid}>
          {slateData.projects.map((p, i) => (
            <div key={i} style={styles.card}>
              <div style={{ height: '260px', backgroundImage: `url(${p.image})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'grayscale(100%) contrast(1.2) brightness(0.6)' }} />
              <div style={{ padding: '30px' }}>
                <span className={p.status === "IN DEVELOPMENT" ? "live-tag" : ""} style={{ fontSize: '0.7rem', color: '#fff', border: '1px solid currentColor', padding: '4px 10px', fontWeight: 'bold' }}>
                  {p.status === "IN DEVELOPMENT" ? "● " + p.status : p.status}
                </span>
                <h3 style={{ fontSize: '1.6rem', margin: '20px 0 10px 0', letterSpacing: '0.05em' }}>{p.title}</h3>
                <p style={{ color: '#666', fontSize: '0.95rem', lineHeight: '1.7' }}>{p.description}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section style={styles.section}>
        <div style={{ borderLeft: '2px solid #00aaff', paddingLeft: '50px' }}>
          <span style={styles.accent}>Leadership</span>
          <h2 style={{ fontSize: '3rem', fontWeight: '900' }}>Michael Kuper</h2>
          <p style={{ maxWidth: '750px', fontSize: '1.15rem', lineHeight: '1.9', color: '#aaa', marginTop: '30px' }}>
            With over 15 years in professional production, Michael Kuper is an award-winning filmmaker and screenwriter.
            From national retail distribution at Walmart to network projects with NBC and HBO,
            Michael applies industry-standard rigor to the independent landscape.
          </p>
          <div style={{ marginTop: '30px' }}>
            {slateData.awards.map((a, i) => <div key={i} style={{ color: '#fff', fontSize: '0.95rem', marginBottom: '10px' }}>— {a}</div>)}
          </div>
        </div>
      </section>

      <section id="support" style={{ ...styles.section, backgroundColor: '#080808' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '40px', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={styles.accent}>Support the Work</span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: '900', marginBottom: '20px' }}>MAKE A TAX-DEDUCTIBLE GIFT</h2>
            <div style={{ minHeight: '100px' }}>
               <p style={{ color: '#888', fontSize: '1rem', lineHeight: '1.7', marginBottom: '30px' }}>
                Your contribution directly funds the development, production, and distribution of independent films through the Aperture Slate pipeline.
              </p>
            </div>
            <div style={{ ...styles.donateBox, width: '100%', boxSizing: 'border-box', height: '600px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '0.9rem', color: '#666', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '20px' }}>
                  Fiscal Sponsor: Fractured Atlas
                </p>
                <a href={slateData.fracturedAtlasUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'block', marginBottom: '16px' }}>
                  <img alt="Donate now at Fractured Atlas!" src="https://fundraising.fracturedatlas.org/images/fundraiser-share-buttons/share-button--dark--alpha.png" style={{ width: '100%', display: 'block' }} />
                </a>
                <a href={slateData.fracturedAtlasUrl} target="_blank" rel="noopener noreferrer" className="btn-hover" style={styles.btnOutline}>
                  Donate via Fractured Atlas
                </a>
              </div>
              <div style={{ borderTop: '1px solid #1a1a1a', paddingTop: '20px' }}>
                <p style={styles.fiscalNote}>
                  Contributions for the charitable purposes of Aperture Slate must be made payable to "Fractured Atlas" only and are tax-deductible to the extent permitted by law.
                </p>
                <p style={styles.checkNote}>
                  <strong style={{ color: '#fff' }}>Donating by check?</strong><br />
                  Checks must be made payable to <strong style={{ color: '#fff' }}>Fractured Atlas</strong>. Please write <strong style={{ color: '#fff' }}>"Aperture Slate"</strong> in the memo line.
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={styles.accent}>Operational Strategy</span>
            <h2 style={{ fontSize: '2.5rem', fontWeight: '900', marginBottom: '20px' }}>THE SLATE ENGINE DECK</h2>
            <div style={{ minHeight: '100px' }}>
              <p style={{ color: '#888', fontSize: '1rem', lineHeight: '1.7', marginBottom: '30px' }}>
                Download our full visual guide to the $1M Production Investment Model and see how we are building a self-sustaining future for independent cinema.
              </p>
            </div>
            <div style={{ ...styles.downloadBox, width: '100%', boxSizing: 'border-box', height: '600px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                <div>
                  <h3 style={{ fontSize: '1.2rem', margin: '0 0 15px 0', letterSpacing: '0.1em', fontWeight: '900' }}>APERTURE SLATE PROSPECTUS</h3>
                  <p style={{ fontSize: '1rem', color: '#aaa', lineHeight: '1.6', marginBottom: '25px' }}>
                    A comprehensive overview of our production revenue reinvestment strategy, fiscal sponsorship oversight, and active 2026 slate.
                  </p>
                </div>
                <div style={{ marginTop: 'auto' }}>
                  <a href={slateData.prospectusUrl} download className="btn-hover" style={{ ...styles.btn, width: '75%', margin: 0 }}>
                    Download PDF Deck
                  </a>
                </div>
            </div>
          </div>
        </div>
      </section>

      <footer style={{ padding: '80px 10%', borderTop: '1px solid #1a1a1a', backgroundColor: '#050505' }}>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <img src="/logo.png" alt="Aperture Slate Footer" style={{ height: '30px', opacity: 0.2 }} />
        </div>
        <p style={{ fontSize: '0.8rem', color: '#333', letterSpacing: '0.2em', textAlign: 'center', margin: 0 }}>
          © 2026 APERTURE SLATE // PERMANENT PRODUCTION COLLECTIVE
        </p>
      </footer>
    </div>
  );
}

// --- ROOT ROUTER ---
export default function App() {
  return (
    <HashRouter>
      <Routes>
        <Route path="/" element={<MainSite />} />
        <Route path="/submit" element={<FilmInitiative />} />
      </Routes>
    </HashRouter>
  );
}